import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import {
  Box,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useGlobalContext } from '../../GlobalProvider';
import { getMaps, getEntitiesByAppId, getEntityById } from '../../api';
import EntityDrawer from './EntityDrawer';
import KPIDashboard from './KPIDashboard';
import LayerTogglePanel from './LayerTogglePanel';
import { 
  createBaseMapSources, 
  createBaseMapLayers, 
  createBaseMapLayerTree, 
  createBaseMapLayerVisibility 
} from './baseMapLayers';
import { 
  createOvertureSources, 
  createOvertureLayers, 
  createOvertureLayerTree, 
  createOvertureLayerVisibility 
} from './overtureMapLayers';
import { 
  createGeoJSONSources, 
  createGeoJSONLayers, 
  createGeoJSONLayerTree, 
  createGeoJSONLayerVisibility 
} from './geojsonLayers';
import { 
  createEntitySources, 
  createEntityLayers, 
  createEntityLayerVisibility,
  assignRandomAlertStates,
  assignStaticAlertStates
} from './entityLayers';




const OvertureMap = () => {
  const mapRef = useRef(null);
  const { fullApp } = useGlobalContext();

  const [mapList, setMapList] = useState([]);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [layerTree, setLayerTree] = useState(createBaseMapLayerTree());
  const [layerVisibility, setLayerVisibility] = useState(createBaseMapLayerVisibility());

  const [showFilters, setShowFilters] = useState(false);
  const [showKPI, setShowKPI] = useState(true);
  const [enable3D, setEnable3D] = useState(true);
  const [enableRippleEffects, setEnableRippleEffects] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const [entityLayers, setEntityLayers] = useState({});

  // New state for entity popup
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityDetails, setEntityDetails] = useState(null);
  const [loadingEntityDetails, setLoadingEntityDetails] = useState(false);
  const [showEntityPopup, setShowEntityPopup] = useState(false);
  const [rippleAnimation, setRippleAnimation] = useState(0);
  const [entityAlertStates, setEntityAlertStates] = useState({});

  // ===== CONFIGURATION: Static Entity IDs for Ripple Effects =====
  // Add your specific entity IDs here (2-3 IDs recommended)
  // To find entity IDs: Click on an entity and check the console or entity details
  const [staticRippleEntityIds] = useState([
    '68fadb077a3657993568ee9b',
    '68fadb077a3657993568ee82'
  ]);

  // Add click event handler for entities
  const handleEntityClick = useCallback(async (event) => {
    const map = mapRef.current;
    if (!map) return;

    // Safely get all valid layer IDs that actually exist in the map
    const validLayerIds = Object.keys(entityLayers)
      .map(entityTypeId => {
        const layerInfo = entityLayers[entityTypeId];
        return layerInfo.layerIds;
      })
      .flat()
      .filter(layerId => {
        // Check if the layer actually exists in the map before querying
        try {
          return map.getLayer(layerId) !== undefined;
        } catch (error) {
          console.warn(`Layer ${layerId} does not exist in map:`, error);
          return false;
        }
      });

    // Only query if we have valid layers
    if (validLayerIds.length === 0) {
      console.log('No valid layers found for entity click:', {
        entityLayersCount: Object.keys(entityLayers).length,
        entityLayersKeys: Object.keys(entityLayers),
        allLayerIds: Object.keys(entityLayers).map(entityTypeId => entityLayers[entityTypeId].layerIds).flat()
      });
      return;
    }

    // Query for features at the clicked point
    const features = map.queryRenderedFeatures(event.point, {
      layers: validLayerIds
    });

    if (features.length > 0) {
      const feature = features[0];
      const entityId = feature.properties.id;

      if (entityId) {
        setSelectedEntity(feature.properties);
        setLoadingEntityDetails(true);
        setShowEntityPopup(true);

        try {
          // Fetch detailed entity information including sensors
          const response = await getEntityById(entityId);
          setEntityDetails(response.data);
        } catch (error) {
          console.error("Failed to fetch entity details:", error);
        } finally {
          setLoadingEntityDetails(false);
        }
      }
    }
  }, [entityLayers]);

  
  // Toggle visibility for all entity layers
  const setAllEntitiesVisibility = useCallback((isVisible) => {
    const map = mapRef.current;
    if (!map || !entityLayers || Object.keys(entityLayers).length === 0) return;

    const updatedVisibility = {};
    Object.values(entityLayers).forEach((layerInfo) => {
      layerInfo.layerIds.forEach((layerId) => {
        try {
          map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
          updatedVisibility[layerId] = isVisible;
        } catch (error) {
          console.warn('Failed to set visibility for layer', layerId, error);
        }
      });
    });
    setLayerVisibility((prev) => ({ ...prev, ...updatedVisibility }));
  }, [entityLayers]);

  // Compute All checkbox state
  const allEntityLayerIds = Object.values(entityLayers || {}).flatMap((info) => info.layerIds || []);
  const allChecked = allEntityLayerIds.length > 0 && allEntityLayerIds.every((layerId) => layerVisibility[layerId]);
  const someChecked = allEntityLayerIds.some((layerId) => layerVisibility[layerId]);
  

  const fetchEntities = async () => {
    if (!fullApp) return;

    try {
      const response = await getEntitiesByAppId(fullApp._id, {
        page: 1,
        limit: 1000,
      });
      const entitiesData = response.data.data;
      setEntities(entitiesData);
      setEntitiesLoaded(true);
      
      // Use static entity IDs for ripples instead of random assignment
      // If staticRippleEntityIds is empty, no ripples will be shown
      const newAlertStates = assignStaticAlertStates(entitiesData, staticRippleEntityIds);
      setEntityAlertStates(newAlertStates);
      
      console.log('=== Ripple Configuration ===');
      console.log('Total entities:', entitiesData.length);
      console.log('Entities with ripples:', staticRippleEntityIds.length);
      console.log('Ripple entity IDs:', staticRippleEntityIds);
      
      // Log available entity IDs and names for easy configuration
      console.log('=== Available Entities (for configuration) ===');
      entitiesData.slice(0, 10).forEach(entity => {
        console.log(`ID: ${entity._id}, Name: ${entity.name}`);
      });
    } catch (err) {
      console.error("Failed to fetch entities:", err);
    }
  };

  useEffect(() => {
    if (fullApp) {
      fetchEntities();
    }
  }, [fullApp, staticRippleEntityIds]);

  // Fetch map list on mount
  useEffect(() => {
    const fetchMaps = async () => {
      try {
        const { data } = await getMaps();
        if (data.success && Array.isArray(data.data)) {
          setMapList(data.data);
          setMapsLoaded(true);
        }
      } catch (err) {
        // setSnackbar({ open: true, message: 'Failed to load maps', severity: 'error' });
      }
    };
    if (fullApp) {
      fetchMaps();
    }
  }, [fullApp]);

  useEffect(() => {
    // Initialize the map only once after required data is loaded
    if (mapRef.current) return;
    if (!fullApp || !mapsLoaded || !entitiesLoaded) return;
    
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);

    // Prepare sources and layers
    console.log('Creating entity sources with:', {
      entitiesCount: entities?.length || 0,
      entityAlertStatesCount: Object.keys(entityAlertStates).length,
      fullAppId: fullApp?._id
    });
    const { sources: entitySources, entityLayersByType } = entities && entities.length > 0 ? createEntitySources(entities, entityAlertStates) : { sources: {}, entityLayersByType: {} };
    const sources = {
      ...createBaseMapSources(),
      ...createOvertureSources(fullApp),
      ...createGeoJSONSources(mapList),
      ...entitySources
    };

    const layers = [
      ...createBaseMapLayers(),
      ...createOvertureLayers(fullApp),
      ...createGeoJSONLayers(mapList),
      ...(entityLayersByType ? createEntityLayers(entityLayersByType) : [])
    ];



    // Create layer tree structure
    const newLayerTree = {
      ...createBaseMapLayerTree(),
      ...createOvertureLayerTree(fullApp),
      ...createGeoJSONLayerTree(mapList)
    };

    // Update layer visibility with Overture, GeoJSON, and Entity layers
    const overtureLayerVisibility = createOvertureLayerVisibility(fullApp);
    const geojsonLayerVisibility = createGeoJSONLayerVisibility(mapList);
    const entityLayerVisibility = entityLayersByType ? createEntityLayerVisibility(entityLayersByType) : {};
    setLayerVisibility(prev => ({
      ...prev,
      ...overtureLayerVisibility,
      ...geojsonLayerVisibility,
      ...entityLayerVisibility
    }));

    // Store entity layers for later use
    console.log('Updating entityLayers state:', {
      entityLayersByTypeKeys: Object.keys(entityLayersByType || {}),
      entityLayersByType: entityLayersByType
    });
    setEntityLayers(entityLayersByType || {});





    // Note: IOT Entities are now handled separately in the top bar, not in the layer tree



    // Update layer tree state
    setLayerTree(newLayerTree);

    const map = new maplibregl.Map({
      container: "map",
      style: {
        version: 8,
        sources,
        layers,
        glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf"
      },
      center: [fullApp.geolocation?.lng || -95.977838, fullApp.geolocation?.lat || 30.080891],
      zoom: 18,
      pitch: 60,
      bearing: -20
    });

    // Load entity type icons
    const loadEntityIcons = async () => {
      if (!entityLayersByType || Object.keys(entityLayersByType).length === 0) return;
      
      const iconPromises = Object.values(entityLayersByType).map(async (layerInfo) => {
        const iconName = layerInfo.entityTypeIcon;
        if (iconName) {
          try {
            // Create a canvas to generate icon
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 32;
            canvas.height = 32;

            // Create a simple icon based on entity type
            ctx.fillStyle = layerInfo.entityTypeColor;
            ctx.fillRect(0, 0, 32, 32);
            ctx.fillStyle = '#ffffff';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(iconName.charAt(0).toUpperCase(), 16, 16);

            // Convert canvas to blob and add to map
            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              map.loadImage(url, (error, image) => {
                if (!error && image) {
                  map.addImage(iconName, image);
                }
              });
            });
          } catch (error) {
            console.error(`Failed to load icon for ${iconName}:`, error);
          }
        }
      });

      await Promise.all(iconPromises);
    };

    map.on('load', () => {
      loadEntityIcons();
    });

    mapRef.current = map;
    setMapInstance(map);

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      // Clear any existing intervals
      if (window.rippleInterval) {
        clearInterval(window.rippleInterval);
        window.rippleInterval = null;
      }
    };
  }, [fullApp, mapsLoaded, entitiesLoaded, mapList, entities, entityAlertStates]);

  // Handle click event listener setup when entityLayers changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !entityLayers || Object.keys(entityLayers).length === 0) return;

    console.log('Setting up click handler for entity layers:', Object.keys(entityLayers));

    // Add click event listener for entities
    map.on('click', handleEntityClick);

    // Cleanup function to remove event listener
    return () => {
      if (map) {
        map.off('click', handleEntityClick);
      }
    };
  }, [entityLayers, handleEntityClick]);

  // Handle ripple animation effect
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !entityLayers || !enableRippleEffects) return;

    //console.log('Ripple animation triggered:', rippleAnimation, 'Layers:', Object.keys(entityLayers));

    // Apply ripple animation to all entity ripple layers
    Object.values(entityLayers).forEach((layerInfo) => {
      const rippleLayerId = layerInfo.layerIds[0]; // ripple layer is now at index 0

      // Check if the layer exists before trying to access it
      try {
        if (map.getLayer(rippleLayerId)) {
          // Create smooth pulsing effect by animating opacity and radius
          const animationProgress = (rippleAnimation % 30) / 30; // 3 second cycle (30 * 100ms = 3000ms)

          // Use smooth easing function for better transitions
          const easeInOut = (t) => {
            return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
          };

          // Add a more sophisticated easing for smoother ripples
          const smoothEase = (t) => {
            return 0.5 * (1 - Math.cos(Math.PI * t));
          };

          const easedProgress = smoothEase(animationProgress);

          // Bigger opacity range for more visibility
          const opacity = Math.sin(easedProgress * Math.PI) * 0.7 + 0.15; // 0.15 to 0.85
          const radiusMultiplier = Math.sin(easedProgress * Math.PI) * 0.6 + 0.4; // 0.4 to 1.0

          // Set opacity with smooth transitions (clamped to valid range 0-1)
          const clampedOpacity = Math.max(0, Math.min(1, opacity));
          const clampedStrokeOpacity = Math.max(0, Math.min(1, opacity * 2.0));
          
          map.setPaintProperty(rippleLayerId, 'circle-opacity', clampedOpacity);
          map.setPaintProperty(rippleLayerId, 'circle-stroke-opacity', clampedStrokeOpacity);

          // Also animate stroke width for more dynamic effect
          const strokeWidth = Math.sin(easedProgress * Math.PI) * 2 + 3; // 3 to 5
          map.setPaintProperty(rippleLayerId, 'circle-stroke-width', strokeWidth);

          // Animate radius based on zoom level with bigger scaling
          const currentZoom = map.getZoom();
          const baseRadius = Math.max(15, Math.min(60, currentZoom * 4)); // 15m to 60m base radius
          const animatedRadius = baseRadius * radiusMultiplier;

          map.setPaintProperty(rippleLayerId, 'circle-radius', animatedRadius);

         // console.log('Animated ripple layer:', rippleLayerId, 'opacity:', opacity, 'radius:', animatedRadius);
        } else {
          console.log('Ripple layer not found:', rippleLayerId);
        }
      } catch (error) {
        console.error('Error animating ripple layer:', rippleLayerId, error);
      }
    });
  }, [rippleAnimation, entityLayers, enableRippleEffects]);

  // Manage ripple animation interval
  useEffect(() => {
    //console.log('Ripple interval management:', { enableRippleEffects, entityLayersCount: entityLayers ? Object.keys(entityLayers).length : 0 });

    if (enableRippleEffects && entityLayers && Object.keys(entityLayers).length > 0) {
      // Start ripple animation with smoother transitions
      if (!window.rippleInterval) {
        window.rippleInterval = setInterval(() => {
          setRippleAnimation(prev => prev + 1);
        }, 50); // Every 100ms for smoother animation
        console.log('Ripple interval started');
      }
    } else {
      // Clear interval if ripple effects are disabled or no entities
      if (window.rippleInterval) {
        clearInterval(window.rippleInterval);
        window.rippleInterval = null;
        console.log('Ripple interval cleared');
      }
    }

    // Cleanup on unmount
    return () => {
      if (window.rippleInterval) {
        clearInterval(window.rippleInterval);
        window.rippleInterval = null;
      }
    };
  }, [enableRippleEffects, entityLayers]);



  return (
    <Box sx={{ height: "90vh", width: "100%", position: "relative" }}>
      {/* Add CSS for ripple animation */}
      <style>
        {`
          @keyframes ripple-pulse {
            0% {
              opacity: 0.2;
              transform: scale(0.6);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.0);
            }
            100% {
              opacity: 0.2;
              transform: scale(0.6);
            }
          }
          
          .ripple-layer {
            transition: all 0.1s ease-in-out;
          }
        `}
      </style>

      {/* IOT Entities Selection Bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 101,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          p: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        {/* Left side - IOT Entities */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {Object.keys(entityLayers).length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  border: '2px solid #999',
                  mr: 1,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#999',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      indeterminate={someChecked && !allChecked}
                      onChange={(e) => setAllEntitiesVisibility(e.target.checked)}
                    />
                  }
                  label={'All'}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.75rem',
                      color: '#333',
                      fontWeight: '600'
                    }
                  }}
                />
              </Box>
              {Object.entries(entityLayers).map(([entityTypeId, layerInfo]) => (
                <Box
                  key={entityTypeId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    border: `2px solid ${layerInfo.entityTypeColor}`,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: layerInfo.entityTypeColor,
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={layerInfo.layerIds.every(layerId => layerVisibility[layerId])}
                        onChange={(e) => {
                          layerInfo.layerIds.forEach(layerId => {
                            mapRef.current?.setLayoutProperty(layerId, 'visibility', e.target.checked ? 'visible' : 'none');
                            setLayerVisibility(prev => ({ ...prev, [layerId]: e.target.checked }));
                          });
                        }}
                      />
                    }
                    label={layerInfo.entityTypeName}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.75rem',
                        color: '#333',
                        fontWeight: '500'
                      }
                    }}
                  />
                </Box>
              ))}
            </>
          )}
        </Box>

        {/* Right side - Filter and KPI buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => setShowKPI(prev => !prev)}
            color="primary"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }
            }}
          >
            <DashboardIcon />
          </IconButton>
          <IconButton
            onClick={() => setShowFilters(prev => !prev)}
            color="primary"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }
            }}
          >
            <FilterListIcon />
          </IconButton>
        </Box>
      </Box>

      {/* KPI Dashboard */}
      {showKPI && (
        <Box sx={{ position: 'absolute', top: 60, right: 16, zIndex: 100, maxWidth: '80%' }}>
          <KPIDashboard entities={entities} fullApp={fullApp} />
        </Box>
      )}

      {/* Layer Toggle Filters */}
      <LayerTogglePanel
        showFilters={showFilters}
        layerTree={layerTree}
        layerVisibility={layerVisibility}
        expandedNodes={expandedNodes}
        setExpandedNodes={setExpandedNodes}
        setLayerVisibility={setLayerVisibility}
        mapRef={mapRef}
        enable3D={enable3D}
        setEnable3D={setEnable3D}
      />

      {/* Map Container */}
      <Box id="map" sx={{ height: "100%", width: "100%" }} />

      {/* Entity Side Drawer */}
      <EntityDrawer
        open={showEntityPopup}
        onClose={() => setShowEntityPopup(false)}
        selectedEntity={selectedEntity}
        entityDetails={entityDetails}
        loadingEntityDetails={loadingEntityDetails}
      />
    </Box>
  );
};

export default OvertureMap;